import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Label } from 'Components/frameworks/formik/formik-input/Label';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { themes } from './FormControl.themes';
type Props = {
  children?: ReactNode;
  className?: string;
  dir?: string | null | undefined;
  hasClear?: boolean;
  hasValidationIcon?: boolean;
  iconLeft?: IconName;
  id?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string | ReactNode;
  notes?: string;
  onClear?: (arg0: React.SyntheticEvent) => void;
  theme?: ThemeKey<typeof themes>;
  validationState?: string;
};
export const FormControl = ({
  children,
  className,
  dir,
  hasClear,
  hasValidationIcon = true,
  iconLeft,
  id,
  isActive,
  isDisabled,
  isFilled,
  isLoading,
  isReadOnly,
  isRequired,
  label,
  notes,
  onClear,
  theme = 'default',
  validationState,
}: Props) => {
  if (!Children.count(children)) {
    return null;
  }

  const {
    thClearButton,
    thClearIconSize,
    thField,
    thSpinner,
    thValidationIcon,
    thValidationIconName,
    thValidationIconSize,
  } = themes[theme]({
    hasValidationIcon,
    isActive,
    isDisabled,
    isFilled,
    isReadOnly,
    validationState,
  }) as DefaultThemeStyles<typeof themes>;
  return (
    <div className={className} dir={dir}>
      {!!label && (
        <Label
          className="mx-15 mb-5"
          htmlFor={id}
          isRequired={isRequired}
          label={label}
          labelAdditionalInfo={notes}
        />
      )}
      <div className={thField}>
        {iconLeft && <Icon name={iconLeft} width="1.25rem" />}
        {children}
        {isLoading && !isDisabled && !isReadOnly && <Spinner className={thSpinner} size="small" />}
        {hasClear && !isDisabled && !isLoading && !isReadOnly && (
          <button
            className={thClearButton}
            data-testid="FormControlClear"
            onClick={onClear}
            type="button"
          >
            <Icon name={ICONS.CROSS} width={thClearIconSize} />
          </button>
        )}
        {thValidationIconName && (
          <Icon
            className={thValidationIcon}
            name={thValidationIconName}
            width={thValidationIconSize}
          />
        )}
      </div>
    </div>
  );
};
